import React, { useState, useEffect } from 'react';
import { COVER_BG_IMAGE } from '../constants/cover';
import { matchesAPI, teamsAPI } from '../services/api';
import { getLogoUrl } from '../utils/logoUrl';
import { FaTrophy, FaArrowUp, FaArrowDown, FaMinus } from 'react-icons/fa';
import Footer from '../components/Footer';
import LoadingSpinner from '../components/LoadingSpinner';
import { useLanguage } from '../context/LanguageContext';

const StandingsPage = () => {
  const { language } = useLanguage();
  const [standings, setStandings] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedGroup, setSelectedGroup] = useState('all');

  useEffect(() => {
    loadStandings();
  }, []);

  const loadStandings = async () => {
    try {
      setLoading(true);
      const [matchesRes, teamsRes] = await Promise.all([
        matchesAPI.getAll(),
        teamsAPI.getAll()
      ]);

      const matches = matchesRes.data;
      const teams = teamsRes.data;

      // Calculer les statistiques par équipe
      const teamStats = {};

      teams.forEach(team => {
        teamStats[team.id] = {
          id: team.id,
          name: team.name,
          logo_url: team.logo_url,
          group: team.group_number || 1,
          played: 0,
          won: 0,
          drawn: 0,
          lost: 0,
          goalsFor: 0,
          goalsAgainst: 0,
          goalDifference: 0,
          points: 0,
          penalty_points: team.penalty_points || 0
        };
      });

      // Traiter les matchs terminés
      matches
        .filter(m => m.status === 'finished')
        .forEach(match => {
          const homeTeam = teamStats[match.home_team_id];
          const awayTeam = teamStats[match.away_team_id];

          if (homeTeam && awayTeam) {
            homeTeam.played++;
            awayTeam.played++;
            homeTeam.goalsFor += match.home_score;
            homeTeam.goalsAgainst += match.away_score;
            awayTeam.goalsFor += match.away_score;
            awayTeam.goalsAgainst += match.home_score;

            if (match.home_score > match.away_score) {
              homeTeam.won++;
              homeTeam.points += 3;
              awayTeam.lost++;
            } else if (match.home_score < match.away_score) {
              awayTeam.won++;
              awayTeam.points += 3;
              homeTeam.lost++;
            } else {
              homeTeam.drawn++;
              awayTeam.drawn++;
              homeTeam.points += 1;
              awayTeam.points += 1;
            }

            homeTeam.goalDifference = homeTeam.goalsFor - homeTeam.goalsAgainst;
            awayTeam.goalDifference = awayTeam.goalsFor - awayTeam.goalsAgainst;
          }
        });

      // Appliquer les pénalités de points
      Object.values(teamStats).forEach(team => {
        team.points = team.points - (team.penalty_points || 0);
      });

      // Organiser par groupe
      const standingsByGroup = {};
      Object.values(teamStats).forEach(team => {
        const group = team.group;
        if (!standingsByGroup[group]) {
          standingsByGroup[group] = [];
        }
        standingsByGroup[group].push(team);
      });

      // Trier chaque groupe
      Object.keys(standingsByGroup).forEach(group => {
        standingsByGroup[group].sort((a, b) => {
          if (b.points !== a.points) return b.points - a.points;
          if (b.goalDifference !== a.goalDifference) return b.goalDifference - a.goalDifference;
          return b.goalsFor - a.goalsFor;
        });
      });

      setStandings(standingsByGroup);
    } catch (error) {
      console.error('Erreur:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  const text = {
    ar: {
      title: 'الترتيب العام',
      subtitle: 'ترتيب الفرق حسب المجموعات',
      group: 'المجموعة',
      allGroups: 'كل المجموعات',
      rank: 'الترتيب',
      team: 'الفريق',
      played: 'لعب',
      won: 'فاز',
      drawn: 'تعادل',
      lost: 'خسر',
      goalsFor: 'له',
      goalsAgainst: 'عليه',
      goalDiff: 'الفارق',
      points: 'النقاط',
      form: 'الشكل'
    },
    fr: {
      title: 'Classement Général',
      subtitle: 'Classement des équipes par groupe',
      group: 'Groupe',
      allGroups: 'Tous les groupes',
      rank: 'Pos',
      team: 'Équipe',
      played: 'J',
      won: 'G',
      drawn: 'N',
      lost: 'P',
      goalsFor: 'BP',
      goalsAgainst: 'BC',
      goalDiff: 'Diff',
      points: 'Pts',
      form: 'Forme'
    }
  };

  const t = text[language];
  const groups = Object.keys(standings).sort((a, b) => a - b);

  return (
    <div style={styles.container}>
      {/* Header */}
      <section style={styles.header}>
        <FaTrophy style={styles.headerIcon} />
        <h1 style={styles.title}>{t.title}</h1>
        <p style={styles.subtitle}>{t.subtitle}</p>
      </section>

      {/* Group Filter */}
      <section style={styles.filterSection}>
        <div style={styles.filterButtons}>
          <button
            onClick={() => setSelectedGroup('all')}
            style={{
              ...styles.filterBtn,
              ...(selectedGroup === 'all' ? styles.filterBtnActive : {})
            }}
          >
            {t.allGroups}
          </button>
          {groups.map(group => (
            <button
              key={group}
              onClick={() => setSelectedGroup(group)}
              style={{
                ...styles.filterBtn,
                ...(selectedGroup === group ? styles.filterBtnActive : {})
              }}
            >
              {t.group} {group}
            </button>
          ))}
        </div>
      </section>

      {/* Standings Tables */}
      <section style={styles.standingsSection}>
        {groups
          .filter(group => selectedGroup === 'all' || selectedGroup === group)
          .map(group => (
            <div key={group} style={styles.groupContainer}>
              <div style={styles.groupHeader}>
                <h2 style={styles.groupTitle}>
                  {t.group} {group}
                </h2>
                <span style={styles.groupTeamCount}>
                  {standings[group].length} {language === 'ar' ? 'فرق' : 'équipes'}
                </span>
              </div>

              <div className="table-responsive" style={styles.tableWrapper}>
                <table className="standings-table" style={styles.table}>
                  <thead>
                    <tr style={styles.tableHeader}>
                      <th style={{ ...styles.th, width: '50px' }}>{t.rank}</th>
                      <th style={{ ...styles.th, textAlign: 'left', minWidth: '200px' }}>{t.team}</th>
                      <th style={styles.th}>{t.played}</th>
                      <th style={styles.th}>{t.won}</th>
                      <th style={styles.th}>{t.drawn}</th>
                      <th style={styles.th}>{t.lost}</th>
                      <th style={styles.th}>{t.goalsFor}</th>
                      <th style={styles.th}>{t.goalsAgainst}</th>
                      <th style={styles.th}>{t.goalDiff}</th>
                      <th style={{ ...styles.th, fontWeight: 'bold' }}>{t.points}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {standings[group].map((team, index) => {
                      const n = standings[group].length;
                      const isPromotion = index <= 1;
                      const isThreatened = (n === 10 && index === 6) || (n === 11 && index === 8);
                      const isRelegated = (n === 10 && (index === 7 || index === 8)) || (n === 11 && index === 9);
                      const isVeryLast = (n === 10 && index === 9) || (n === 11 && index === 10);
                      const rowStyle = isPromotion ? styles.firstPlace
                        : isVeryLast ? styles.veryLastPlace
                          : isRelegated ? styles.lastPlace
                            : isThreatened ? styles.threatenedPlace : {};
                      return (
                        <tr
                          key={team.id}
                          style={{ ...styles.tableRow, ...rowStyle }}
                        >
                          <td style={styles.td}>
                            <div style={styles.rankCell}>
                              <span style={styles.rankNumber}>{index + 1}</span>
                              {index === 0 && <FaArrowUp style={styles.rankIconUp} />}
                              {index === standings[group].length - 1 && <FaArrowDown style={styles.rankIconDown} />}
                            </div>
                          </td>
                          <td style={{ ...styles.td, textAlign: 'left' }}>
                            <div style={styles.teamCell}>
                              {getLogoUrl(team.logo_url) && (
                                <img
                                  src={getLogoUrl(team.logo_url)}
                                  alt={team.name}
                                  style={styles.teamLogo}
                                  onError={(e) => e.target.style.display = 'none'}
                                />
                              )}
                              <span style={styles.teamName}>{team.name}</span>
                            </div>
                          </td>
                          <td style={styles.td}>{team.played}</td>
                          <td style={styles.td}>{team.won}</td>
                          <td style={styles.td}>{team.drawn}</td>
                          <td style={styles.td}>{team.lost}</td>
                          <td style={styles.td}>{team.goalsFor}</td>
                          <td style={styles.td}>{team.goalsAgainst}</td>
                          <td style={{
                            ...styles.td,
                            color: team.goalDifference > 0 ? '#28a745' :
                              team.goalDifference < 0 ? '#dc3545' : '#666'
                          }}>
                            {team.goalDifference > 0 ? '+' : ''}{team.goalDifference}
                          </td>
                          <td style={{ ...styles.td, fontWeight: 'bold', fontSize: '16px' }}>
                            {team.points}
                            {team.penalty_points > 0 && (
                              <div style={{ fontSize: '10px', color: '#dc3545', marginTop: '2px' }}>
                                (-{team.penalty_points} Pts)
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Legend */}
              <div style={styles.legend}>
                <div style={styles.legendItem}>
                  <div style={{ ...styles.legendBox, background: '#d4edda' }}></div>
                  <span>{language === 'ar' ? 'الصاعد' : 'Promotion'}</span>
                </div>
                <div style={styles.legendItem}>
                  <div style={{ ...styles.legendBox, background: '#fff3cd' }}></div>
                  <span>{language === 'ar' ? 'مهدد بالسقوط' : 'Menacé'}</span>
                </div>
                <div style={styles.legendItem}>
                  <div style={{ ...styles.legendBox, background: '#f8d7da' }}></div>
                  <span>{language === 'ar' ? 'هبوط' : 'Relégation'}</span>
                </div>
                <div style={styles.legendItem}>
                  <div style={{ ...styles.legendBox, background: 'rgba(220, 53, 69, 0.5)' }}></div>
                  <span>{language === 'ar' ? 'المركز الأخير' : 'Dernière place'}</span>
                </div>
              </div>
            </div>
          ))}
      </section>

      <Footer />
    </div>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    background: '#f5f5f5',
    width: '100%',
    maxWidth: '100vw',
    overflowX: 'hidden'
  },
  header: {
    backgroundImage: `linear-gradient(135deg, rgba(26, 54, 93, 0.52) 0%, rgba(44, 82, 130, 0.48) 50%, rgba(30, 77, 122, 0.48) 100%), url(${COVER_BG_IMAGE})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    padding: 'clamp(40px, 8vw, 80px) clamp(16px, 4vw, 20px) 60px',
    textAlign: 'center',
    color: '#fff',
    width: '100%'
  },
  headerIcon: {
    fontSize: '60px',
    marginBottom: '20px',
    animation: 'pulse 2s infinite'
  },
  title: {
    fontSize: 'clamp(28px, 5vw, 48px)',
    fontWeight: 'bold',
    marginBottom: '15px',
    textShadow: '2px 2px 4px rgba(0,0,0,0.3)'
  },
  subtitle: {
    fontSize: 'clamp(14px, 2.5vw, 20px)',
    opacity: 0.9
  },
  filterSection: {
    width: '100%',
    maxWidth: '1400px',
    margin: '0 auto',
    padding: '20px 16px',
    boxSizing: 'border-box'
  },
  filterButtons: {
    display: 'flex',
    gap: '10px',
    flexWrap: 'wrap',
    justifyContent: 'center',
    width: '100%'
  },
  filterBtn: {
    padding: '12px 25px',
    background: 'white',
    border: '2px solid #e0e0e0',
    borderRadius: '25px',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s',
    color: '#333'
  },
  filterBtnActive: {
    background: '#1a365d',
    color: '#fff',
    borderColor: '#1a365d'
  },
  standingsSection: {
    width: '100%',
    maxWidth: '1400px',
    margin: '0 auto',
    padding: '0 16px 60px',
    boxSizing: 'border-box'
  },
  groupContainer: {
    background: 'white',
    borderRadius: '20px',
    padding: 'clamp(16px, 4vw, 30px)',
    marginBottom: '40px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
    overflow: 'hidden'
  },
  groupHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '25px',
    paddingBottom: '15px',
    borderBottom: '3px solid #1a365d'
  },
  groupTitle: {
    fontSize: '28px',
    fontWeight: 'bold',
    color: '#333',
    margin: 0
  },
  groupTeamCount: {
    fontSize: '16px',
    color: '#666'
  },
  tableWrapper: {
    overflowX: 'auto',
    WebkitOverflowScrolling: 'touch',
    width: '100%'
  },
  table: {
    width: '100%',
    minWidth: '600px',
    borderCollapse: 'collapse'
  },
  tableHeader: {
    background: '#c41e3a',
    borderBottom: '2px solid #dee2e6'
  },
  th: {
    padding: '15px 10px',
    textAlign: 'center',
    fontSize: '13px',
    fontWeight: '600',
    color: '#ffffff',
    textTransform: 'uppercase'
  },
  tableRow: {
    borderBottom: '1px solid #f0f0f0',
    transition: 'background 0.3s'
  },
  firstPlace: {
    background: 'rgba(212, 237, 218, 0.3)'
  },
  secondPlace: {
    background: 'rgba(212, 237, 218, 0.15)'
  },
  lastPlace: {
    background: 'rgba(248, 215, 218, 0.5)'
  },
  veryLastPlace: {
    background: 'rgba(220, 53, 69, 0.5)',
    color: '#000'
  },
  threatenedPlace: {
    background: 'rgba(255, 193, 7, 0.25)'
  },
  td: {
    padding: '15px 10px',
    textAlign: 'center',
    fontSize: '14px',
    color: '#333'
  },
  rankCell: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '5px'
  },
  rankNumber: {
    fontWeight: 'bold',
    fontSize: '16px'
  },
  rankIconUp: {
    color: '#28a745',
    fontSize: '12px'
  },
  rankIconDown: {
    color: '#dc3545',
    fontSize: '12px'
  },
  teamCell: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px'
  },
  teamLogo: {
    width: '30px',
    height: '30px',
    objectFit: 'contain'
  },
  teamName: {
    fontWeight: '600',
    fontSize: '14px'
  },
  legend: {
    display: 'flex',
    gap: '20px',
    marginTop: '20px',
    padding: '15px',
    background: '#f8f9fa',
    borderRadius: '10px',
    justifyContent: 'center'
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '13px'
  },
  legendBox: {
    width: '20px',
    height: '20px',
    borderRadius: '4px'
  }
};

// Animation CSS
const styleSheet = document.createElement('style');
styleSheet.textContent = `
  @keyframes pulse {
    0%, 100% { transform: scale(1); }
    50% { transform: scale(1.1); }
  }

  .tableRow:hover {
    background: rgba(26, 54, 93, 0.06) !important;
  }

  @media (max-width: 768px) {
    .table {
      font-size: 12px;
    }
    .th, .td {
      padding: 8px 5px !important;
    }
    .teamName {
      font-size: 12px !important;
    }
  }
`;
document.head.appendChild(styleSheet);

export default StandingsPage;
